/**
 * Shared chart dialog for exposed value history.
 * Provide `translationPrefix` for module-specific labels.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Badge } from "@/shared/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Loader2, Minus, TrendingDown, TrendingUp, Zap } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useLanguage } from "@/core/LanguageContext";

interface MetricChartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mappingId: string;
  displayName: string;
  unit: string | null;
  currentValue?: number | string | null;
  translationPrefix?: string;
}

type TimeRange = "24h" | "7d" | "30d";

export const MetricChartDialog = ({
  open,
  onOpenChange,
  mappingId,
  displayName,
  unit,
  currentValue,
  translationPrefix = "core.metricChart",
}: MetricChartDialogProps) => {
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState<TimeRange>("24h");

  const { data: historicalData, isLoading, error, refetch } = useQuery({
    queryKey: ["historical-data", mappingId, timeRange],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("influxdb-query", {
        body: {
          historical: true,
          mappingId,
          timeRange,
        },
      });

      if (error) throw error;
      return (data?.data as { timestamp: string; value: number }[]) || [];
    },
    enabled: open && !!mappingId,
  });

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [timeRange, open, refetch]);

  const chartData =
    historicalData?.map((point) => ({
      ...point,
      time: new Date(point.timestamp).getTime(),
      formattedTime: formatTimeLabel(point.timestamp, timeRange),
    })) || [];

  const stats = calculateStats(historicalData || []);

  function formatTimeLabel(timestamp: string, range: TimeRange): string {
    const date = new Date(timestamp);
    switch (range) {
      case "24h":
        return format(date, "HH:mm");
      case "7d":
        return format(date, "EEE HH:mm");
      case "30d":
        return format(date, "MMM dd");
      default:
        return format(date, "HH:mm");
    }
  }

  function calculateStats(data: { value: number }[]) {
    if (data.length === 0) {
      return { min: null, max: null, avg: null, trend: "stable" as const };
    }

    const values = data.map((datum) => datum.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, value) => sum + value, 0) / values.length;

    const mid = Math.floor(values.length / 2);
    const firstHalfAvg = values.slice(0, mid).reduce((sum, value) => sum + value, 0) / mid;
    const secondHalfAvg = values.slice(mid).reduce((sum, value) => sum + value, 0) / (values.length - mid);

    let trend: "up" | "down" | "stable" = "stable";
    const changePercent = ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100;
    if (changePercent > 5) trend = "up";
    else if (changePercent < -5) trend = "down";

    return { min, max, avg, trend };
  }

  const TrendIcon = stats.trend === "up" ? TrendingUp : stats.trend === "down" ? TrendingDown : Minus;
  const trendColor =
    stats.trend === "up"
      ? "text-destructive"
      : stats.trend === "down"
      ? "text-success"
      : "text-muted-foreground";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-warning" />
            {displayName}
          </DialogTitle>
          <DialogDescription>
            {t(`${translationPrefix}.description`, "Historical data visualization")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {currentValue !== null && currentValue !== undefined && (
                <Badge variant="outline" className="text-lg px-3 py-1">
                  {t(`${translationPrefix}.current`, "Current")}:
                  <span className="font-bold ml-1">
                    {typeof currentValue === "number" ? currentValue.toFixed(2) : currentValue}
                  </span>
                  {unit && <span className="ml-1 text-muted-foreground">{unit}</span>}
                </Badge>
              )}
            </div>
            <Tabs value={timeRange} onValueChange={(value) => setTimeRange(value as TimeRange)}>
              <TabsList>
                <TabsTrigger value="24h">{t(`${translationPrefix}.range.24h`, "24 Hours")}</TabsTrigger>
                <TabsTrigger value="7d">{t(`${translationPrefix}.range.7d`, "7 Days")}</TabsTrigger>
                <TabsTrigger value="30d">{t(`${translationPrefix}.range.30d`, "30 Days")}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="h-[300px] w-full">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full text-destructive">
                {t(`${translationPrefix}.error`, "Failed to load historical data")}
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                {t(`${translationPrefix}.empty`, "No data available for this time range")}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="formattedTime"
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${value.toFixed(1)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "var(--popover)",
                      borderColor: "var(--border)",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "var(--popover-foreground)" }}
                    formatter={(value: number) => [`${value.toFixed(3)} ${unit || ""}`, displayName]}
                    labelFormatter={(label) => `${t(`${translationPrefix}.label`, "Time")}: ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="var(--primary)"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorValue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <StatCard
              label={t(`${translationPrefix}.stat.min`, "Minimum")}
              value={stats.min}
              unit={unit}
            />
            <StatCard
              label={t(`${translationPrefix}.stat.max`, "Maximum")}
              value={stats.max}
              unit={unit}
            />
            <StatCard
              label={t(`${translationPrefix}.stat.avg`, "Average")}
              value={stats.avg}
              unit={unit}
            />
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">
                {t(`${translationPrefix}.stat.trend`, "Trend")}
              </p>
              <div className={`mt-2 flex items-center gap-2 ${trendColor}`}>
                <TrendIcon className="h-4 w-4" />
                <span className="text-sm font-semibold capitalize">{stats.trend}</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface StatCardProps {
  label: string;
  value: number | null;
  unit: string | null;
}

const StatCard = ({ label, value, unit }: StatCardProps) => (
  <div className="rounded-lg border p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-2 text-lg font-semibold">
      {value === null ? "--" : value.toFixed(2)}
      {unit && <span className="text-xs font-normal text-muted-foreground ml-1">{unit}</span>}
    </p>
  </div>
);
